import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { Agent } from '@/types/agent.types';
import type { WebSearchToggleState } from './buildEffectiveWebSearchConfig';

export type { WebSearchToggleState } from './buildEffectiveWebSearchConfig';

export interface UseWebSearchToggleResult {
  state: WebSearchToggleState;
  setEnabled: (enabled: boolean) => void;
  setUseAdvanced: (useAdvanced: boolean) => void;
  isVisible: boolean;
  advancedAvailable: boolean;
}

const STORAGE_KEY = 'stellar_web_search_toggles';
type StoreMap = Record<string, WebSearchToggleState>;

function readStorage(): StoreMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as StoreMap) : {};
  } catch {
    return {};
  }
}

// Module-level mutable store. New object reference on every change so
// useSyncExternalStore re-renders subscribers. Persisted to localStorage
// on each write; subscribers are notified after persist.
let inMemory: StoreMap = readStorage();
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inMemory));
  } catch {
    // localStorage unavailable (private mode / quota). State stays in
    // memory; we deliberately swallow the error.
  }
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function getSnapshot(): StoreMap {
  return inMemory;
}

function writeState(agentId: string, next: WebSearchToggleState): void {
  inMemory = {
    ...inMemory,
    [agentId]: next,
  };
  persist();
  notify();
}

/**
 * TESTING ONLY. Clears the in-memory store so each test starts fresh.
 * Do not call from production code.
 */
export function _resetStoreForTests(): void {
  inMemory = readStorage();
  // After clear(), readStorage returns {} so inMemory matches the empty
  // localStorage state and existing subscribers re-render with that.
  notify();
}

export function useWebSearchToggle(
  agentId: string | undefined,
  agent: Agent | undefined
): UseWebSearchToggleResult {
  const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isVisible = !!agent?.webSearchConfig?.enabled;
  const advancedAvailable =
    agent?.webSearchConfig?.provider === 'tavily' &&
    agent.webSearchConfig.searchDepth === 'advanced';

  const stored = agentId ? map[agentId] : undefined;
  const seedEnabled =
    agent?.webSearchConfig?.enabled === true &&
    agent.webSearchConfig.defaultEnabled !== false;
  const state: WebSearchToggleState = useMemo(
    () =>
      stored ?? {
        enabled: seedEnabled,
        useAdvanced:
          agent?.webSearchConfig?.provider === 'tavily' &&
          agent.webSearchConfig.searchDepth === 'advanced',
      },
    [stored, seedEnabled, agent]
  );

  const setEnabled = useCallback(
    (enabled: boolean) => {
      if (agentId) writeState(agentId, { ...state, enabled });
    },
    [agentId, state]
  );

  const setUseAdvanced = useCallback(
    (useAdvanced: boolean) => {
      if (agentId) writeState(agentId, { ...state, useAdvanced });
    },
    [agentId, state]
  );

  return { state, setEnabled, setUseAdvanced, isVisible, advancedAvailable };
}
